/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { AbstractControl } from '@angular/forms';
import { MatOption } from '@angular/material/core';

export function getMultiSelectDisplayValueHelper(field: AbstractControl, lookupData: any[], displayValue: string): any {
	if (!Array.isArray(field.value)) {
		return null;
	}

	if (field.value.filter((item) => item === 0).length > 0) {
		return displayValue;
	} else {
		return field.value ? lookupData?.filter((t) => field.value.indexOf(t.id) >= 0).map((x) => ' ' + x.text) : null;
	}
}

export function resetMultiSelectsHelper(field: AbstractControl, lookupData: any[]) {
	selectAllItems(field, lookupData);
}

export function multiSelectSelectionChangedHelper(selected: boolean, field: AbstractControl, lookupData: any[]) {
	if (!selected) {
		field.setValue(field.value.filter((i) => i !== 0));
	} else {
		if (field.value.length === lookupData.length) {
			field.value.push(0);
			field.setValue(field.value);
		}
	}

	// reselects the 'All' option if every item is selected
	if (field.value && field.value.filter((item) => item === 0).length > 0) {
		selectAllItems(field, lookupData);
	}
}

export function toggleAllMultiSelectSelectionHelper(selection: MatOption, field: AbstractControl, lookupData: any[]) {
	if (selection?.selected) {
		selectAllItems(field, lookupData);
	} else {
		field.patchValue([]);
	}
}

// To be used with persisting filters,
// if an item is added to a persisted list then this will check if all items should be selected and will tick any new items
export function SelectAllIfAllOptionSelectedHelper(selection: MatOption, field: AbstractControl, lookupData: any[]) {
	if (selection?.selected) {
		selectAllItems(field, lookupData);
	}
}

export function selectAllItems(field: AbstractControl, lookupData: any[]) {
	field.patchValue([...lookupData.map((item) => item.id), 0]);
}

// multi selects filter if 0 isnt included in the array - i.e. (all option not selected)
// and the array length is greater than 0 - i.e. (something selected)
export function canFilterMultiselect(field: string, dataForm: any) {
	return dataForm.controls[field].value && dataForm.controls[field].value.length > 0 && !dataForm.controls[field].value.includes(0);
}

// grouped multi select helpers
export function toggleAllMultiSelectSelectionGroupedHelper(selection: MatOption, field: AbstractControl, lookupData: any[]) {
	if (selection?.selected) {
		selectAllGroupedItems(field, lookupData);
	} else {
		field.patchValue([]);
	}
}
export function selectAllGroupedItems(field: AbstractControl, lookupData: any[]) {
	if (!Array.isArray(lookupData) || lookupData.length === 0) {
		field.patchValue([0]);
		return;
	}

	const allIds = lookupData.flatMap((group) => group.items.map((item) => item.id));
	field.patchValue([...allIds, 0]);
}
export function groupedMultiSelectSelectionChangedHelper(selected: boolean, field: AbstractControl, lookupData: any[]) {
	if (!Array.isArray(field.value)) {
		field.setValue([]);
	}

	if (!selected) {
		field.setValue(field.value.filter((i) => i !== 0));
	} else {
		if (field.value.length === getGroupedItemsCount(lookupData)) {
			field.value.push(0);
			field.setValue(field.value);
		}
	}

	// reselects the 'All' option if every item is selected
	if (field.value && field.value.filter((item) => item === 0).length > 0) {
		selectAllGroupedItems(field, lookupData);
	}
}
function getGroupedItemsCount(lookupData: any[]): number {
	if (!Array.isArray(lookupData)) {
		return 0;
	}

	return lookupData.reduce((acc, group) => acc + (group.items?.length || 0), 0);
}
export function getGroupedMultiSelectDisplayValueHelper(field: AbstractControl, lookupData: any[], displayValue: string): any {
	if (!Array.isArray(field.value)) {
		return null;
	}

	if (field.value && field.value.filter((item) => item === 0).length > 0) {
		return displayValue;
	} else {
		if (!Array.isArray(lookupData)) {
			return null;
		}

		// Flatten all child items
		const allItems = lookupData.flatMap((group) => group.items);
		// Filter by selected ids and map to names
		return field.value ? allItems.filter((item) => field.value.includes(item.id)).map((item) => ' ' + item.name) : null;
	}
}
